import { useMemo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { bool, func, } from 'prop-types';
import normalize from 'normalize-strings';
import keys from 'lodash/keys';
import pickBy from 'lodash/pickBy'
import findIndex from 'lodash.findindex';
import Button from '../../StoraEnsoComponents/Button';
import DataTable from '../../DataTable';
import getColumns from './columns';
import TableExportFunctions from '../../DataTable/TableExportFunctions/millTableExportFunctions';
import RowDetailView from './rowDetailView';
import { useDroneDetailContext } from '../../Contexts/DroneDetailContext';
import { TableActions, ActionGroup } from '../../DataTable/TableActions';
import { ReactComponent as CalculatorSvg } from '../../../assets/icons/calculator-simple.svg';
import ReactTooltip from 'react-tooltip';
import DataFilter from './dataFilter';

const Wrapper = styled.div`
  overflow-x: auto;
`;

const CalculateIcon = styled(CalculatorSvg)``;
const TableFooter = styled.div`
`;

const TopPanel = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

function FeatureGrid({ loading, onCalculateAllFeatures , projectData}) {
  const [selectedRowIdx, setSelectedRowIdx] = useState(null);
  const [filtered, setFiltered] = useState(null);
  const [gridSearch, setGridSearch] = useState('');
  const [gridTypeFilter, setGridTypeFilter] = useState(null);

  const { featuresData, selectedFeature, setSelectedFeature, setDeletedFeature } = useDroneDetailContext();

  const onItemDelete = selectedId => setDeletedFeature(featuresData.find(f => f.properties.id === selectedId));

  const columns = useMemo(() => getColumns({onItemDelete}), [onItemDelete]);

  const handleRowSelect = ({data: {id}}) => {
    setSelectedFeature(featuresData.find(el => el.properties.id === id));
  };

  const gridData = useMemo(() => {
    if (featuresData) {
        const data = filtered || featuresData;
        return data.map(d => ({...d.properties, feature_type: d.geometry.type}));
    } else {
        return [];
    }
  }, [featuresData, filtered]);

  useEffect(() => {
    const idx = findIndex(gridData, el => el.id === selectedFeature?.properties.id);

    if(idx > -1) {
      setSelectedRowIdx(idx);
    }

  }, [selectedFeature]);

  useEffect(() => {
    const activeTypes = keys(pickBy(gridTypeFilter, value => value === true));

    if(!loading && (gridSearch.trim().length > 0 || (gridTypeFilter && activeTypes.length !== 3))) {
      const regex = new RegExp(normalize(gridSearch), 'gi');
      let tempData = featuresData;

      if(activeTypes.length === 0) {
        setFiltered(null);
      }

      if(activeTypes.length !== 3) {
        tempData = featuresData.filter(item => {

          // area type check
          if(activeTypes.includes('area') && item.geometry.type === 'Polygon') {
            return true;
          }

          // profile type check
          if(activeTypes.includes('profile') && item.geometry.type === 'LineString' && [0, 2].includes(item.properties.profile_type)) {
            return true;
          }

          // AI type check
          if(activeTypes.includes('ai') && item.geometry.type === 'LineString' && item.properties.profile_type === 3) {
            return true;
          }

          return false;
        });
      }

      setFiltered(tempData.filter(item => ((normalize(String(item.properties.id) || '')).match(regex) || (normalize(item.properties.name || '')).match(regex))));

    } else {
      setFiltered(null);
    }
  }, [loading, gridSearch, gridTypeFilter]);

  return (
    <>
      <Wrapper>
        <ReactTooltip type='light'/>
          <TopPanel>
            <DataFilter
                onSearchChange={value => setGridSearch(value)}
                onTypeChange={value => setGridTypeFilter(value)}
              />
            <TableActions>
              <ActionGroup>
                <Button icon={<CalculateIcon />} disabled={loading} onClick={onCalculateAllFeatures} size="sm">Calculate</Button>
              </ActionGroup>
              <TableExportFunctions tableData={featuresData ? featuresData.map( t=>t.properties) : []} headers={ columns.filter(col => col.Header)}  projectData={projectData}/>
            </TableActions>
          </TopPanel>
          <DataTable
            columns={columns}
            data={gridData}
            minWidth="1050px"
            loading={loading}
            selectedRowIndex={selectedRowIdx}
            onRowSelect={handleRowSelect}
            detailView={RowDetailView}
          />
      </Wrapper>
      <TableFooter>
        <div className="total_volume">Total volume: {((featuresData ? featuresData.map( t=>t.properties.volume_m3) : []).reduce((a, b) => parseFloat(a) + parseFloat(b), 0)).toFixed(2)} m<sup>3</sup></div>
        <div className="total_volume-coef">Total volume with coefficients: {((featuresData ? featuresData.map( t=>t.properties.volume_coef_m3) : []).reduce((a, b) => parseFloat(a) + parseFloat(b), 0)).toFixed(2)} m<sup>3</sup></div>
      </TableFooter>
    </>
  );
}

FeatureGrid.propTypes = {
  loading: bool.isRequired,
  onCalculateAllFeatures: func.isRequired,
}

export default FeatureGrid;